import { Info, RotateCcw } from "lucide-react";

export const DEFAULT_RULES = {
  AA: { min: 2, max: 10 }, A: { min: 2, max: 7 }, BB: { min: 1, max: 1 }, B: { min: 1, max: 1 },
  CC: { min: 1, max: 1 }, C: { min: 0, max: 0 }, D: { min: 0, max: 0 },
};
const VD_CATS = new Set(["AA", "A"]);

export const ruleShort = (k, r) => (VD_CATS.has(k) ? `VD × ${r.min} / VD × ${r.max}` : `${r.min} / ${r.max}`);

export const ruleError = (r) => {
  const mn = Number(r.min), mx = Number(r.max);
  if (r.min === "" || r.max === "" || Number.isNaN(mn) || Number.isNaN(mx)) return "Escribe un número";
  if (mn < 0 || mx < 0 || mn > 999 || mx > 999) return "Entre 0 y 999";
  if (mn > mx) return "El mínimo supera al máximo";
  return null;
};

const RULE_LEGEND = [
  ["AA", "Se vende cada día", "más de 365 uds/año"],
  ["A", "Se vende cada semana", "52 a 365 uds/año"],
  ["BB", "Se vende cada decena", "35 a 51 uds/año"],
  ["B", "Se vende cada mes", "12 a 34 uds/año"],
  ["CC", "Se vende cada trimestre", "4 a 11 uds/año"],
  ["C", "Se vende alguna vez al año", "1 a 3 uds/año"],
  ["D", "Sin ventas", "0 uds/año"],
];

function RuleInput({ code, field, value, onChange }) {
  return (
    <input
      type="number" min="0" max="999" step={VD_CATS.has(code) ? "0.5" : "1"} value={value}
      onChange={(e) => onChange(code, field, e.target.value)}
      data-testid={`minmax-rule-${code}-${field}`}
      className="w-14 border border-slate-300 rounded px-1.5 py-0.5 text-xs font-mono-num text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-900"
    />
  );
}

function RuleRow({ code, label, range, rule, styleCls, onChange }) {
  const err = ruleError(rule);
  const vd = VD_CATS.has(code);
  return (
    <div className="flex items-start gap-2" data-testid={`minmax-rule-${code}`}>
      <span className={`shrink-0 w-8 text-center text-[11px] font-bold rounded border py-0.5 ${styleCls}`}>{code}</span>
      <div className="text-xs leading-snug">
        <div className="text-slate-800">
          <span className="font-medium">{label}</span> <span className="text-slate-400">·</span>{" "}
          <span className="font-mono text-slate-500">{range}</span>
        </div>
        <div className="flex flex-wrap items-center gap-1 text-slate-500 mt-1">
          Min = {vd && "ventas diarias ×"} <RuleInput code={code} field="min" value={rule.min} onChange={onChange} />
          <span className="text-slate-300">·</span>
          Max = {vd && "ventas diarias ×"} <RuleInput code={code} field="max" value={rule.max} onChange={onChange} />
        </div>
        {err && <div className="text-rose-600 mt-0.5" data-testid={`minmax-rule-${code}-error`}>{err}</div>}
      </div>
    </div>
  );
}

export function RuleLegend({ rules, catStyle, onChange, onReset, custom, recalculating }) {
  return (
    <div className="mt-4 bg-white border border-slate-200 rounded-lg px-4 py-3" data-testid="minmax-rule-legend">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
          <Info className="h-4 w-4 text-slate-500" /> Regla de cálculo
        </div>
        <span className="text-xs text-slate-500">Cambia los valores y todo se recalcula al momento. Solo para esta consulta.</span>
        {recalculating && <span className="text-xs text-sky-700" data-testid="minmax-recalculating">Recalculando…</span>}
        {custom && (
          <button onClick={onReset} data-testid="minmax-rules-reset"
            className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-slate-700 border border-slate-300 rounded-md px-2.5 py-1 hover:bg-slate-50">
            <RotateCcw className="h-3 w-3" /> Valores por defecto
          </button>
        )}
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-3 mt-3">
        {RULE_LEGEND.map(([k, label, range]) => (
          <RuleRow key={k} code={k} label={label} range={range} rule={rules[k]} styleCls={catStyle[k]} onChange={onChange} />
        ))}
      </div>
    </div>
  );
}
